import { useState } from 'react';

interface StalenessLineProps {
  generatedAt: string | null;
}

function relativeAge(iso: string, nowMs: number): string | null {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const hours = Math.floor((nowMs - then) / 3_600_000);
  if (hours < 1) return 'less than an hour ago';
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * "Data as of {generated_at}" — so a dead crawler doesn't masquerade as a
 * quiet market. The timestamp comes from the dataset itself (CHO-705's
 * updated_at), not from when this page fetched it.
 */
export default function StalenessLine({ generatedAt }: StalenessLineProps) {
  // Render-time purity: capture "now" once at mount instead of calling
  // Date.now() on every render.
  const [nowMs] = useState(() => Date.now());
  if (!generatedAt) {
    return <p className="text-sm text-amber-700">Dataset has no timestamp — staleness unknown.</p>;
  }
  const parsed = new Date(generatedAt);
  const absolute = Number.isNaN(parsed.getTime())
    ? generatedAt
    : parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  const relative = relativeAge(generatedAt, nowMs);
  const stale = relative != null && nowMs - parsed.getTime() > 30 * 3_600_000;
  return (
    <p className={`text-sm ${stale ? 'text-amber-700 font-medium' : 'text-gray-500'}`}>
      Data as of {absolute}
      {relative && <span className="text-gray-400"> ({relative})</span>}
      {stale && <span className="ml-1">— the crawler may be behind.</span>}
    </p>
  );
}
